// The customer's home: what is live, how answers are coming in, and one-click routes to the
// everyday pages. All numbers arrive pre-aggregated from /survey/costumer/dashboard_info (plain
// COUNT/GROUP BY — the page never receives response rows), and the chart is plain SVG like the
// report visuals (ReportTypes/ReportViz) — no chart library builds on this CRA.
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from 'moment';
import { CallApi } from 'utils/Request';
import Toast from 'views/Toast/Toast';
import LoadingModal from "components/Loading/Loading";
import { loadProfile } from 'views/Services/UserService';
import { KpiCard, MONO } from "views/Survey/ReportTypes/ReportViz";

const GREEN = '#2f9e44';
const cardStyle = {
  background: '#fff', border: '1px solid #e2e8e4', borderRadius: 14,
  boxShadow: '0 1px 2px rgba(20,40,30,.06),0 6px 20px rgba(20,40,30,.05)',
  marginBottom: 16, overflow: 'hidden',
};
const cardHead = { padding: '13px 18px', borderBottom: '1px solid #e2e8e4', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' };
const cardTitle = { fontWeight: 700, fontSize: 15, color: '#1b2320' };
const selectStyle = { fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: '#1b2320', background: '#fff', border: '1px solid #cfd8d3', borderRadius: 9, padding: '7px 10px', cursor: 'pointer' };

// ---- the monthly bar chart (single series → one hue, no legend; the title names it) ----
function MonthlyBars({ series, t }) {
  const [hover, setHover] = useState(null);
  if (!series.length) return <div style={{ padding: 24, color: '#8a988f', fontSize: 14 }}>{t('No responses yet')}</div>;
  const W = 760, H = 210, padL = 8, padB = 26, padT = 24;
  const max = Math.max(1, ...series.map(s => s.count));
  const bw = (W - padL * 2) / series.length;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      {series.map((s, i) => {
        const h = Math.round((H - padB - padT) * s.count / max);
        const x = padL + i * bw + bw * 0.18, w = bw * 0.64;
        const y = H - padB - h;
        return (
          <g key={i} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
            {/* generous invisible hit target so the tooltip works on thin bars too */}
            <rect x={padL + i * bw} y={padT} width={bw} height={H - padB - padT} fill="transparent">
              <title>{`${s.label}: ${s.count}`}</title>
            </rect>
            {s.count > 0 && (
              <rect x={x} y={y} width={w} height={Math.max(h, 2)} rx="3"
                fill={GREEN} opacity={hover === null || hover === i ? 1 : 0.45} style={{ pointerEvents: 'none', transition: 'opacity .12s' }} />
            )}
            {s.count > 0 && (
              <text x={x + w / 2} y={y - 6} textAnchor="middle" style={{ fontFamily: 'monospace', fontSize: 11.5, fontWeight: 700, fill: '#5d6d66', pointerEvents: 'none' }}>{s.count}</text>
            )}
            <text x={padL + i * bw + bw / 2} y={H - 8} textAnchor="middle" style={{ fontSize: 11, fill: '#8a988f', pointerEvents: 'none' }}>{s.label}</text>
          </g>
        );
      })}
      <line x1={padL} y1={H - padB} x2={W - padL} y2={H - padB} stroke="#e2e8e4" strokeWidth="1" />
    </svg>
  );
}

function DashboardPage(props) {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const user = useSelector(s => s.user);
  const [mysurveys, setMysurveys] = useState([]);
  const [stats, setStats] = useState(null);
  const [chartYear, setChartYear] = useState('last12');
  const [tableYear, setTableYear] = useState('all');

  useEffect(() => {
    setIsLoading(true);
    CallApi({ url: 'survey/costumer/dashboard_info', method: 'get' }).then((res) => {
      if (res.success) {
        setMysurveys(res.data || []);
        setStats(res.stats || null);
      } else {
        Toast(400, res.error);
      }
    }).finally(() => setIsLoading(false));
    loadProfile();
  }, []);

  const titleOf = (s) => {
    const main = s.main || [];
    const m = main.find(x => x.language === (i18n.language || '').slice(0, 2)) || main.find(x => x.language === 'en') || main[0];
    return m ? m.title : 'No Title';
  };

  const perSY = (stats && stats.perSurveyYear) || [];
  const monthly = (stats && stats.monthly) || [];
  const years = Array.from(new Set(perSY.map(r => Number(r.year)))).sort((a, b) => b - a);
  const thisYear = new Date().getFullYear();
  const totalResponses = perSY.reduce((a, r) => a + Number(r.count), 0);
  const responsesThisYear = perSY.filter(r => Number(r.year) === thisYear).reduce((a, r) => a + Number(r.count), 0);

  // chart series: a calendar year (Jan–Dec), or the rolling last 12 months
  const monthCount = (y, m) => { const row = monthly.find(r => Number(r.year) === y && Number(r.month) === m); return row ? Number(row.count) : 0; };
  let series = [];
  if (chartYear === 'last12') {
    for (let i = 11; i >= 0; i--) {
      const d = moment().subtract(i, 'months');
      series.push({ label: d.format('MMM') + (d.month() === 0 || i === 11 ? ' ' + d.format('YY') : ''), count: monthCount(d.year(), d.month() + 1) });
    }
  } else {
    const y = Number(chartYear);
    for (let m = 1; m <= 12; m++) series.push({ label: moment().month(m - 1).format('MMM'), count: monthCount(y, m) });
  }

  // per-survey counts for the selected year ("all" = all time), biggest first
  const surveyRows = mysurveys.map(s => {
    const mine = perSY.filter(r => r.survey_id === s.id);
    const total = mine.reduce((a, r) => a + Number(r.count), 0);
    const inYear = tableYear === 'all' ? total : mine.filter(r => Number(r.year) === Number(tableYear)).reduce((a, r) => a + Number(r.count), 0);
    return { id: s.id, title: titleOf(s), active: !!s.active, count: inYear, total };
  }).sort((a, b) => b.count - a.count);
  const maxRow = Math.max(1, ...surveyRows.map(r => r.count));

  const allowedAnswers = () => {
    const p = user.profile || {};
    return p.free_response ? t('NO LIMITS') : (p.response_remaining != null ? p.response_remaining : '—');
  };

  const go = (path) => () => props.history.push(path);
  const shortcuts = [
    { label: t('Surveys'), icon: '📋', path: '/survey/survey', badge: stats ? stats.surveys.active : null },
    { label: t('Campaigns'), icon: '📨', path: '/campaign/campaign', badge: stats ? stats.campaigns.active : null },
    { label: t('Email Lists'), icon: '👥', path: '/email_list/email_list', badge: stats ? stats.email_lists : null },
    { label: t('Create Link / QR'), icon: '🔗', path: '/sendsurvey/create_link' },
    { label: t('Email settings'), icon: '⚙️', path: '/emailsetting/index' },
    { label: t('Buy more answers'), icon: '🛒', path: '/costumer/purchase_responds' },
    { label: t('Payment details'), icon: '💳', path: '/dashboard/payment_detail' },
    { label: t('Update your info'), icon: '👤', path: '/dashboard/profile' },
  ];

  const statusChip = (active) => (
    <span style={{
      fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em',
      padding: '3px 9px', borderRadius: 99,
      background: active ? '#e7f4ea' : '#f1f3f2', color: active ? '#2f8a45' : '#8a988f',
      border: `1px solid ${active ? '#cfe6d7' : '#e2e8e4'}`, whiteSpace: 'nowrap',
    }}>{active ? t('Active') : t('Inactive')}</span>
  );

  return (
    <div>
      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 14, marginBottom: 16 }}>
        <KpiCard label={t('Active surveys')} value={stats ? stats.surveys.active : '…'}
          delta={stats && stats.surveys.inactive > 0 ? <div style={{ fontSize: 12, color: '#8a988f', marginTop: 4 }}>{stats.surveys.inactive} {t('inactive')}</div> : null} />
        <KpiCard label={t('Total responses')} value={stats ? totalResponses : '…'} />
        <KpiCard label={t('Responses this year')} value={stats ? responsesThisYear : '…'}
          delta={<div style={{ fontSize: 12, color: '#8a988f', marginTop: 4 }}>{thisYear}</div>} />
        <KpiCard label={t('Available answers')} value={allowedAnswers()} />
      </div>

      {/* shortcuts */}
      <div style={{ ...cardStyle, padding: '12px 14px', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {shortcuts.map((s, i) => (
          <button key={i} onClick={go(s.path)} style={{
            fontFamily: 'inherit', fontSize: 13.5, fontWeight: 600, color: '#1b2320', background: '#f7faf8',
            border: '1px solid #e2e8e4', borderRadius: 10, padding: '10px 16px', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 8,
          }}>
            <span>{s.icon}</span>{s.label}
            {s.badge != null && <span style={{ fontFamily: MONO, fontSize: 11.5, fontWeight: 700, background: '#e7f4ea', color: '#2f8a45', borderRadius: 99, padding: '1px 8px' }}>{s.badge}</span>}
          </button>
        ))}
      </div>

      {/* responses over time */}
      <div style={cardStyle}>
        <div style={cardHead}>
          <div style={cardTitle}>📈 {t('Responses per month')}</div>
          <select value={chartYear} onChange={e => setChartYear(e.target.value)} style={selectStyle}>
            <option value="last12">{t('Last 12 months')}</option>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div style={{ padding: '10px 14px 6px' }}>
          <MonthlyBars series={series} t={t} />
        </div>
      </div>

      {/* per-survey table + latest responses */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))', gap: 16, alignItems: 'start' }}>
        <div style={{ ...cardStyle, marginBottom: 0 }}>
          <div style={cardHead}>
            <div style={cardTitle}>📋 {t('Responses per survey')}</div>
            <select value={tableYear} onChange={e => setTableYear(e.target.value)} style={selectStyle}>
              <option value="all">{t('All years')}</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div style={{ padding: '6px 18px 12px' }}>
            {surveyRows.length === 0 && <div style={{ padding: 16, color: '#8a988f', fontSize: 14 }}>{t('No responses yet')}</div>}
            {surveyRows.map(r => (
              <div key={r.id} style={{ padding: '10px 0', borderBottom: '1px solid #eef2f0' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 14, fontWeight: 600, minWidth: 0 }}>
                    <a href={`/survey/view_report/${r.id}`} onClick={(e) => { e.preventDefault(); props.history.push(`/survey/view_report/${r.id}`); }}
                      style={{ color: '#1b2320', textDecoration: 'none' }}
                      title={t('Open report')}>{r.title}</a>
                    <span style={{ marginLeft: 8 }}>{statusChip(r.active)}</span>
                  </div>
                  <div style={{ fontFamily: MONO, fontSize: 12.5, color: '#5d6d66', whiteSpace: 'nowrap' }}>
                    <b style={{ fontSize: 14 }}>{r.count}</b>{tableYear !== 'all' ? ` / ${r.total}` : ''}
                  </div>
                </div>
                <div style={{ height: 7, background: '#f1f3f2', borderRadius: 99, overflow: 'hidden' }}>
                  <div style={{ width: `${Math.round(r.count / maxRow * 100)}%`, height: '100%', background: GREEN, borderRadius: 99 }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ ...cardStyle, marginBottom: 0 }}>
          <div style={cardHead}><div style={cardTitle}>🕘 {t('Latest responses')}</div></div>
          <div style={{ padding: '6px 18px 12px' }}>
            {(!stats || stats.recent.length === 0) && <div style={{ padding: 16, color: '#8a988f', fontSize: 14 }}>{t('No responses yet')}</div>}
            {stats && stats.recent.map(r => {
              const sv = mysurveys.find(s => s.id === r.survey_id);
              return (
                <div key={r.id} style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, padding: '9px 0', borderBottom: '1px solid #eef2f0', flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 13.5, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{sv ? titleOf(sv) : `#${r.survey_id}`}</div>
                    <div style={{ fontSize: 12, color: '#8a988f' }}>{r.email || r.location || t('anonymous')}</div>
                  </div>
                  <div style={{ fontSize: 12, color: '#8a988f', whiteSpace: 'nowrap' }} title={moment(r.created_at).format('LLL')}>
                    {moment(r.created_at).fromNow()}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {isLoading && <LoadingModal />}
    </div>
  );
}

export default DashboardPage;
